/**
 * 🗑 **아이가 쓴 글이 언제 지워지는가** — 보존 기간(FR-CLASS-09).
 *
 * ## 왜 필요한가
 * 아이 글·반 명단·「왔어요」 기록이 **영구히 쌓이고 있었다.** 지우는 길이 아예 없었다
 * (2026-08-26 확인). 이름·연락처를 안 받는 원칙은 지키고 있지만
 * **「언제 지워지나요」에 답이 없는 것**은 다른 문제다.
 *
 * 학교 담당자는 계약 전에 반드시 묻는다. 그때 정직한 답이 「지우는 기능이 없습니다」면
 * **그 자리에서 계약이 멈춘다.** 이건 기능이라기보다 학교 영업의 관문이다.
 *
 * ## 🔴 보호자에게 보여 줄 문장을 **여기서 만든다**
 * 보존 기간은 관리자에서 고르고, 보호자 안내에는 사람이 쓴 문장이 따로 있다 —
 * 그러면 **둘이 갈린다.** 설정은 6개월인데 안내문에는 1년이라고 적혀 있는 상태가
 * 아무 에러 없이 생기고, 그게 제일 나쁜 종류의 거짓말이다.
 * 그래서 안내 문장은 **설정에서 뽑아 쓴다**(`retentionSay`).
 *
 * ## 🔴 기본은 「안 지움」이다
 * 기본을 6개월 같은 값으로 두면, 이 기능을 넣는 순간 **이미 있던 반이 조용히 지워진다.**
 * 운영자가 고르기 전까지는 아무것도 안 지운다. 대신 관리자 화면이 「정하지 않았어요」라고
 * 눈에 띄게 말한다.
 */

/** 0 = 안 지움. 나머지는 「마지막 활동 뒤 N개월」. */
export const RETENTION_CHOICES: readonly { months: number; label: string }[] = [
  { months: 0, label: "안 지워요" },
  { months: 3, label: "3개월 뒤" },
  { months: 6, label: "6개월 뒤" },
  { months: 12, label: "1년 뒤" },
];

export const RETENTION_DEFAULT = 0;

export function sanitizeRetentionMonths(raw: unknown): number {
  return RETENTION_CHOICES.some((c) => c.months === raw) ? (raw as number) : RETENTION_DEFAULT;
}

/**
 * 이 반을 지울 때가 됐나.
 *
 * 🔴 **「마지막 활동」이 기준이다** — 만든 날이 아니라. 학기 중에 쉬는 반(방학·행사)이
 * 만든 날 기준이면 **수업 중에 사라진다.** 아이가 한 글자라도 쓰면 시계가 다시 돈다.
 *
 * 🔴 마지막 활동을 **모르면 안 지운다.** 모르는 것을 오래된 것으로 치면
 * 표가 깨진 날 반이 통째로 날아간다 — 되돌릴 수 없는 일에는 이쪽으로 틀린다.
 */
export function isExpired(lastAt: string | undefined, months: number, now: Date): boolean {
  if (months <= 0) return false;
  if (!lastAt) return false;
  const t = Date.parse(lastAt);
  if (!Number.isFinite(t)) return false;
  const due = new Date(t);
  due.setMonth(due.getMonth() + months);
  return now.getTime() >= due.getTime();
}

/** 며칠 남았나(지난 것은 0). 운영자 화면이 「곧 지워져요」를 말할 수 있게. */
export function daysLeft(lastAt: string | undefined, months: number, now: Date): number | null {
  if (months <= 0 || !lastAt) return null;
  const t = Date.parse(lastAt);
  if (!Number.isFinite(t)) return null;
  const due = new Date(t);
  due.setMonth(due.getMonth() + months);
  return Math.max(0, Math.ceil((due.getTime() - now.getTime()) / 86_400_000));
}

/**
 * 🔴 **보호자에게 보여 줄 한 문장** — 설정에서 뽑는다.
 *
 * 사람이 따로 써 두면 설정과 갈린다. 여기서 만들면 운영자가 기간을 바꾸는 순간
 * 보호자 안내도 같이 바뀐다.
 */
export function retentionSay(months: number): string {
  const m = sanitizeRetentionMonths(months);
  if (m <= 0) {
    return "아이가 만든 그림책은 수업이 끝나도 보관해요. 선생님이 요청하시면 언제든 지워 드려요.";
  }
  const term = m === 12 ? "1년" : `${m}개월`;
  return `아이가 만든 그림책은 마지막으로 쓴 날부터 ${term}이 지나면 저절로 지워져요. 그전에 선생님이 내려받아 두실 수 있어요.`;
}

/** 운영자 화면용 — 지금 무엇이 일어나는지 짧게. */
export function retentionAdminSay(months: number): string {
  const m = sanitizeRetentionMonths(months);
  return m <= 0
    ? "지금은 아무것도 저절로 지워지지 않아요. 학교가 「언제 지워지나요」를 물으면 답할 것이 없어요."
    : `마지막 활동 뒤 ${m === 12 ? "1년" : `${m}개월`}이 지난 반은 저절로 지워져요.`;
}

/**
 * 🕰 **밤 정리가 **멈춘 것**을 알아본다.
 *
 * ## 왜 필요한가
 * 화면은 「마지막 정리 …」 날짜를 보여 준다. 그런데 **성공한 뒤 크론이 죽으면**
 * 그 날짜가 그대로 굳은 채 **평범한 글씨로** 계속 떠 있다 — 운영자는 날짜를 보고
 * 「되고 있구나」로 읽는다. 정작 그 파일의 주석이 경계한 「되고 있겠지」가
 * **한 번도 안 돈 경우와 실패한 경우만** 막고 있었다.
 *
 * 학교에는 「기간이 지나면 지워집니다」라고 말해 둔 상태다. 그 약속이 조용히 깨진다.
 *
 * 🔴 **하루에 한 번 도는 일**이라 이틀이 넘으면 이상한 것이다. 시계가 조금 틀리거나
 * 배포로 한 번 걸러지는 것까지는 봐준다.
 */
export const SWEEP_STALE_MS = 2 * 24 * 60 * 60 * 1000;

/**
 * 마지막 정리가 너무 오래됐나.
 *
 * 🔴 **못 읽는 날짜는 「오래됐다」로 본다** — 「모르니까 괜찮다」로 넘기면
 * 표가 깨진 날 아무도 모른다. 안전한 쪽은 「말해 주는」 쪽이다.
 */
export function sweepIsStale(at: string | undefined, nowMs: number): boolean {
  if (!at) return true;
  const t = Date.parse(at);
  if (!Number.isFinite(t)) return true;
  return nowMs - t > SWEEP_STALE_MS;
}

/** 오래됐을 때 운영자에게 할 말. 멀쩡하면 빈 문자열. */
export function sweepStaleSay(at: string | undefined, nowMs: number): string {
  if (!sweepIsStale(at, nowMs)) return "";
  return "밤 정리가 이틀 넘게 안 돌았어요 — 정한 기간이 안 지켜지고 있어요. 개발자에게 알려 주세요.";
}

/**
 * 🔴 **보존 기간을 줄이는 것은 되돌릴 수 없다.**
 *
 * 관리자 화면에서 기간은 라디오 단추라 **한 번 누르면 바로 저장**된다(안 누른 상태로 두는
 * 편집물이 없어서 그 자체는 좋은 설계다). 그런데 「3개월 뒤」를 잘못 누르면 **그날 밤
 * 서버가 혼자 도는 정리**가 그보다 오래된 반을 통째로 지운다 — 아이가 한 학기 만든 글이다.
 * 눌렀다는 것조차 모를 수 있다.
 *
 * 이미 있던 보호막 둘(기본 「안 지움」 · 마지막 활동을 모르면 안 지움)은 **설정이 주어졌을 때
 * 잘못 지우는 것**을 막는다. **설정을 잘못 바꾸는 것**은 아무도 안 막고 있었다.
 *
 * 그래서 **더 빨리 지우게 되는 쪽으로 바꿀 때만** 한 번 물어본다:
 *  · 안 지움(0) → 아무 기간   — 이제부터 지운다
 *  · 1년 → 3개월             — 더 빨리 지운다
 * 늘리거나 「안 지워요」로 되돌리는 것은 안전하니 안 묻는다(잔소리를 만들지 않는다).
 */
export function retentionNeedsConfirm(prev: unknown, next: unknown): boolean {
  const from = sanitizeRetentionMonths(prev);
  const to = sanitizeRetentionMonths(next);
  if (to === 0) return false; // 「안 지워요」로 가는 것은 언제나 안전하다
  if (from === 0) return true; // 안 지우던 것을 지우기 시작한다
  return to < from; // 더 빨리 지운다
}

/** 물어볼 때 할 말 — **무엇이 지워지는지**와 되돌릴 수 없다는 것을 같이 적는다. */
export function retentionConfirmSay(next: unknown): string {
  const to = sanitizeRetentionMonths(next);
  const label = RETENTION_CHOICES.find((c) => c.months === to)?.label ?? `${to}개월 뒤`;
  return [
    `이제부터 아이 글을 「${label}」 지웁니다.`,
    "",
    `오늘 밤부터 마지막 활동이 ${to}개월보다 오래된 반은 그림책·명단까지 **영구히** 지워집니다.`,
    "되돌릴 수 없어요. 학교와 이야기된 기간이 맞나요?",
  ].join("\n");
}
